import { useState, useEffect, useCallback } from 'react'
import { Profile, DailyLog, HabitType } from '../types/centurion'
import {
  getLocalDateString,
  getDaysBetween,
  generateDailyHabits,
  isLogComplete,
} from './game-logic'

const STORAGE_KEY = 'centurion_profile_v1'
const TIMEZONE = 'Asia/Kolkata'

const defaultProfile: Profile = {
  name: 'Recruit',
  startDate: getLocalDateString(new Date(), TIMEZONE),
  resetCount: 0,
  timezone: TIMEZONE,
  logs: {},
  isDemo: true,
}

export function useCenturionStore() {
  const [profile, setProfile] = useState<Profile | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setProfile(parsed)
      } catch (e) {
        console.error('Failed to parse profile', e)
        setProfile(defaultProfile)
      }
    } else {
      setProfile(defaultProfile)
    }
    setIsLoaded(true)
  }, [])

  // Save to localStorage whenever profile changes
  useEffect(() => {
    if (profile && isLoaded) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile))
    }
  }, [profile, isLoaded])

  // Core Game Loop: Check for missed days and initialize today
  useEffect(() => {
    if (!profile || !isLoaded) return

    const todayStr = getLocalDateString(new Date(), profile.timezone)
    let updatedProfile = { ...profile }
    let needsUpdate = false

    // 1. Check for resets (missed days)
    const daysSinceStart = getDaysBetween(profile.startDate, todayStr)

    if (daysSinceStart > 0) {
      // Check all days from startDate to yesterday
      for (let i = 0; i < daysSinceStart; i++) {
        const checkDate = new Date(`${profile.startDate}T00:00:00Z`)
        checkDate.setDate(checkDate.getDate() + i)
        const checkDateStr = getLocalDateString(checkDate, profile.timezone)

        const log = profile.logs[checkDateStr]
        if (!log || !log.finalized) {
          // MISSED A DAY! RESET!
          console.warn(
            `Missed day on ${checkDateStr}. Resetting to zero. No mercy.`,
          )
          updatedProfile.resetCount += 1
          updatedProfile.startDate = todayStr // Restart today
          updatedProfile.logs = {} // Clear history on reset (or keep it? Rules say "Reset to zero")
          needsUpdate = true
          break // Stop checking, we reset
        }
      }
    }

    // 2. Initialize today's log if it doesn't exist
    const currentDayNumber =
      getDaysBetween(updatedProfile.startDate, todayStr) + 1
    if (!updatedProfile.logs[todayStr]) {
      updatedProfile.logs[todayStr] = {
        date: todayStr,
        habits: generateDailyHabits(currentDayNumber),
        finalized: false,
        meals: [],
        createdAt: new Date().toISOString(),
      }
      needsUpdate = true
    }

    if (needsUpdate) {
      setProfile(updatedProfile)
    }
  }, [profile, isLoaded])

  // Actions
  const updateHabit = useCallback(
    (dateStr: string, habitType: HabitType, value: number | boolean) => {
      setProfile((prev) => {
        if (!prev) return prev
        const log = prev.logs[dateStr]
        if (!log || log.finalized) return prev // Cannot edit finalized logs

        const habit = log.habits[habitType]
        let completed = false

        if (typeof value === 'boolean' && typeof habit.target === 'boolean') {
          completed = value === habit.target
        } else if (
          typeof value === 'number' &&
          typeof habit.target === 'number'
        ) {
          completed = value >= habit.target
        }

        return {
          ...prev,
          logs: {
            ...prev.logs,
            [dateStr]: {
              ...log,
              habits: {
                ...log.habits,
                [habitType]: { ...habit, value, completed },
              },
            },
          },
        }
      })
    },
    [],
  )

  const finalizeDay = useCallback((dateStr: string) => {
    setProfile((prev) => {
      if (!prev) return prev
      const log = prev.logs[dateStr]
      if (!log || log.finalized) return prev

      if (!isLogComplete(log.habits)) {
        alert('Cannot finalize. Not all habits are complete.')
        return prev
      }

      return {
        ...prev,
        logs: {
          ...prev.logs,
          [dateStr]: {
            ...log,
            finalized: true,
            finalizedAt: new Date().toISOString(),
          },
        },
      }
    })
  }, [])

  const resetProgress = useCallback(() => {
    if (
      confirm(
        'Are you sure you want to manually reset? There is no going back.',
      )
    ) {
      setProfile((prev) => {
        if (!prev) return prev
        const todayStr = getLocalDateString(new Date(), prev.timezone)
        return {
          ...prev,
          startDate: todayStr,
          resetCount: prev.resetCount + 1,
          logs: {
            [todayStr]: {
              date: todayStr,
              habits: generateDailyHabits(1),
              finalized: false,
              meals: [],
              createdAt: new Date().toISOString(),
            },
          },
        }
      })
    }
  }, [])

  const updateName = useCallback((name: string) => {
    setProfile((prev) => (prev ? { ...prev, name } : prev))
  }, [])

  return {
    profile,
    isLoaded,
    updateHabit,
    finalizeDay,
    resetProgress,
    updateName,
    todayStr: profile ? getLocalDateString(new Date(), profile.timezone) : '',
  }
}
